import type {
  BloomieTamSeniority,
  CdiJobType,
  FreelanceJobType,
  IJobProperties,
  JobType,
  CRAStatus,
  PaymentTerms,
} from './domain';

/** Codes d’erreur des lignes sentinelle (consultation `getMonthlyTamPrime` uniquement). */
export const PRIME_LINE_ERROR_CODES = {
  MISSION_NOT_FOUND: 'MISSION_NOT_FOUND',
  ASSEMBLY_ERROR: 'ASSEMBLY_ERROR',
  MISSING_PRICING: 'MISSING_PRICING',
  BLOOMER_NOT_FOUND: 'BLOOMER_NOT_FOUND',
  BILLING_ASSEMBLY_ERROR: 'BILLING_ASSEMBLY_ERROR',
} as const;

export type PrimeLineErrorCode = (typeof PRIME_LINE_ERROR_CODES)[keyof typeof PRIME_LINE_ERROR_CODES];

export type PrimeSplitRoles = Required<Pick<IJobProperties, 'passeur' | 'buteur' | 'capitaine' | 'defenseur'>>;

export type BasePrime<J extends JobType = JobType> = {
  jobFunnelProjectId: string;
  airtableId: string;
  clientSocietyName: string;
  bloomerFullName: string;
  jobType: J;
  monthlyMargin: number;
  monthlyPrime: number;
  splitRate: number;
  commissionRate: number;
  split: PrimeSplitRoles;
  errorCode?: PrimeLineErrorCode;
};

export const MISSION_PRIME_ERROR_CRA_STATUS = 'Erreur de chargement';
/** Prime mensuelle mission freelance — voir `computeMissionPrime` (`api.talents`). */
export type MissionPrime = BasePrime<FreelanceJobType> & {
  craStatus: CRAStatus | typeof MISSION_PRIME_ERROR_CRA_STATUS;
  normalWorkedDays: number;
  saturdaysWorkedDays: number;
  sundayAndholidaysWorkedDays: number;
  astreinteWorkedDays: number;
  craAirtableId: string | null;
};

/** Prime mensuelle facturation CDI — voir `computeBillingPrime` (`api.talents`). */
export type BillingPrime = BasePrime<CdiJobType> & {
  paymentTerms?: PaymentTerms;
};

/** Statut d’une régularisation TAM — voir `upsertRegularisationByAdmin` (`api.talents`, TEC-2789). */
export type PrimeRegularisationStatus = 'pending' | 'validated';

/** Régularisation mensuelle TAM persistée — voir `upsertRegularisationByAdmin` (`api.talents`, TEC-2789). */
export type PrimeRegularisation = {
  id: string;
  tam: string;
  monthYear: string;
  total: number;
  reason: string;
  status: PrimeRegularisationStatus;
  createdBy: string;
  modifiedBy: string | null;
  createdAt: Date;
  modifiedAt: Date | null;
};

/** Agrégat consultation mensuelle TAM — voir `computeMonthlyTamPrime` (`api.talents`). */
export type MonthlyTamPrime = {
  primes: (MissionPrime | BillingPrime)[];
  bonus: number;
  regularisation: number;
  regularisationReason: string | null;
  totalFreelanceMarge: number;
  totalBillingMarge: number;
  totalMarge: number;
  totalPrime: number;
  totalDeals: number;
  totalMissions: number;
};

/**
 * Marge mensuelle d'un Bloomie (Sales / TAD) pour un mois donné — PRO-1364.
 * Réutilise le calcul de marge TAM (`computeMonthlyFreelancePrime` + `computeMonthlyBillingPrime`)
 * filtré sur l'email du Bloomie dans les colonnes Split, sans commission ni bonus.
 */
export type MonthlyBloomieMargin = Pick<MonthlyTamPrime, 'totalFreelanceMarge' | 'totalBillingMarge' | 'totalMarge'> & {
  email: string;
};

/** Prime mensuelle validée et figée — voir `getTamPrimes` (`api.talents`, TEC-2811). */
export type ValidatedTamPrime = MonthlyTamPrime & {
  id: string;
  monthYear: string;
  validationDate: Date;
};

/** Agrégat annuel TAM — voir `getTamPrimes` (`api.talents`, TEC-2811). */
export type AnnualTamPrime = {
  tam: string;
  tamSeniority: BloomieTamSeniority;
  goalCompletion: number;
  validatedPrimes: ValidatedTamPrime[];
  pendingPrimes: MonthlyTamPrime | null;
};

/** Première année civile avec marges mensuelles in-app (primes validées / pending). */
export const FIRST_MARGIN_YEAR = 2026;

/**
 * Reliquat de marge cumulée fin 2025 par email TAM (freelance + CDI, hors application).
 * Snapshot figé — les années ≥ FIRST_MARGIN_YEAR s'accumulent via le calcul in-app.
 */
export const TAM_MARGIN_CUMUL_END_2025: Readonly<Record<string, number>> = {
  'auriane.de-lencquesaing@bloomays.com': 2_125_967.32,
  'alix.gastou@bloomays.com': 1_028_506.48,
  'florine.biervoye@bloomays.com': 1_479_667.13,
  'julie.le-dreff@bloomays.com': 621_824.44,
  'romain.nalais@bloomays.com': 638_800.63,
  'lallie.bouchain@bloomays.com': 490_755.51,
  'johan.moscardo@bloomays.com': 197_573.0,
  'taoues.imakhlaf@bloomays.com': 95_622.5,
  'laurelen.hazard@bloomays.com': 5_645.0,
};

/** Cumul lifetime de marge TAM — voir `getLifetimeMarginCumuls` (`api.talents`, TEC-3114). */
export type LifetimeMarginCumul = {
  tam: string;
  totalMargin: number;
  staffEntryDate: Date | null;
};

export const TAM_ANNUAL_MARGIN_OBJECTIVES: Record<Exclude<BloomieTamSeniority, 'Director'>, number> = {
  Apprenti: 109_000,
  Associate: 210_000,
  Confirme: 330_000,
  Senior: 438_000,
  Principal: 558_000,
  Executive: 666_000,
};

/** Seuils annuels de deals par niveau TAM (PRO-1348 / TEC-3092). */
export const TAM_DEALS_SENIORITY_THRESHOLDS: Record<Exclude<BloomieTamSeniority, 'Director'>, number> = {
  Apprenti: 0,
  Associate: 6,
  Confirme: 18,
  Senior: 24,
  Principal: 30,
  Executive: 36,
};

const TAM_SENIORITY_ORDER: Exclude<BloomieTamSeniority, 'Director'>[] = [
  'Apprenti',
  'Associate',
  'Confirme',
  'Senior',
  'Principal',
  'Executive',
];

/** Niveau immédiatement supérieur, ou `null` si Executive / Director. */
export const getNextTamSeniority = (current: BloomieTamSeniority): Exclude<BloomieTamSeniority, 'Director'> | null => {
  if (current === 'Director') {
    return null;
  }
  const currentIndex = TAM_SENIORITY_ORDER.indexOf(current);
  if (currentIndex < 0 || currentIndex >= TAM_SENIORITY_ORDER.length - 1) {
    return null;
  }
  return TAM_SENIORITY_ORDER[currentIndex + 1];
};
